import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { get, post } from "@/shared/api/client";
import type { AccessStatus, IsoDate, IsoDateTime, PathStatus, Role, Uuid } from "@/shared/api/types";

export type AccessAction = "approve" | "reject" | "suspend" | "reinstate";
export type StatusFilter = AccessStatus | "all";

export type AdminUserRow = {
  id: Uuid;
  email: string;
  display_name: string;
  role: Role;
  access_status: AccessStatus;
  created_at: IsoDateTime;
  last_login_at: IsoDateTime | null;
  access_decided_at: IsoDateTime | null;
  last_active_at: IsoDateTime | null;
  study_minutes: number;
  learning: { id: Uuid; title: string; status: PathStatus }[];
};

export type AdminUserList = { counts: Record<StatusFilter, number>; users: AdminUserRow[] };

export type AdminPath = {
  id: Uuid;
  title: string;
  subject: string;
  status: PathStatus;
  target_level: string;
  start_date: IsoDate | null;
  target_end_date: IsoDate | null;
  created_at: IsoDateTime;
  progress: { completed_lessons: number; total_planned_lessons: number; percent: number | null } | null;
  mastery: { mastered: number; total: number; percent: number | null } | null;
  current_phase: string | null;
  current_module: string | null;
  minutes_last_7_days: number | null;
  lessons_overdue: number | null;
};

export type AdminUserDetail = AdminUserRow & {
  timezone: string;
  onboarding_completed_at: IsoDateTime | null;
  access_note: string | null;
  access_decided_by: string | null;
  paths: AdminPath[];
  history: { action: string; at: IsoDateTime; by: string | null; note: string | null }[];
};

export function useAdminUsers(status: StatusFilter, query: string, enabled = true) {
  const params = new URLSearchParams();
  if (status !== "all") params.set("status", status);
  if (query.trim()) params.set("q", query.trim());
  return useQuery({
    queryKey: ["admin", "users", status, query.trim()],
    queryFn: () => get<AdminUserList>(`/api/v1/admin/users?${params}`),
    enabled,
    refetchInterval: 60_000,
  });
}

export function useAdminUser(userId: string) {
  return useQuery({
    queryKey: ["admin", "user", userId],
    queryFn: () => get<AdminUserDetail>(`/api/v1/admin/users/${userId}`),
  });
}

export function useAccessDecision(userId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ action, note }: { action: AccessAction; note?: string }) =>
      post<AdminUserDetail>(`/api/v1/admin/users/${userId}/${action}`, { note: note?.trim() || null }),
    onSuccess: (detail) => {
      queryClient.setQueryData(["admin", "user", userId], detail);
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });
}
